import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { getCurrentUser } from '@/services/auth';
import {
  ADMIN_LOGIN_PATH,
  ADMIN_TOKEN_STORAGE_KEY,
  type AuthScope,
  clearStoredToken,
  getLoginPath,
  getStoredToken,
  resolveApiAuthScope,
  resolveRouteAuthScope,
  SUB_TOKEN_STORAGE_KEY,
} from '@/services/authScope';
import { getCurrentSubAccount } from '@/services/subAuth';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { stringify } from 'querystring';
import React, { useEffect } from 'react';
import defaultSettings from '../config/defaultSettings';

type InitialState = {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  currentSubAccount?: API.SubAccountProfile;
  currentAuthScope?: AuthScope;
  loading?: boolean;
  fetchAdminUserInfo?: () => Promise<API.CurrentUser | undefined>;
  fetchSubUserInfo?: () => Promise<API.SubAccountProfile | undefined>;
};

const ScopedSessionSync: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key !== ADMIN_TOKEN_STORAGE_KEY &&
        event.key !== SUB_TOKEN_STORAGE_KEY
      ) {
        return;
      }

      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return <>{children}</>;
};

const buildRedirectSearch = (): string => {
  const { pathname, search } = history.location;

  return `?${stringify({ redirect: pathname + search })}`;
};

const redirectToLogin = (
  scope: Exclude<AuthScope, 'public'>,
  withRedirect = false,
): void => {
  history.replace({
    pathname: getLoginPath(scope),
    search: withRedirect ? buildRedirectSearch() : '',
  });
};

const hasPendingScopedIdentity = (
  scope: Exclude<AuthScope, 'public'>,
  state: Pick<InitialState, 'currentUser' | 'currentSubAccount'> | undefined,
): boolean => {
  if (scope === 'sub') {
    return !!getStoredToken('sub') && !state?.currentSubAccount;
  }

  return !!getStoredToken('admin') && !state?.currentUser;
};

export async function getInitialState(): Promise<InitialState> {
  const fetchAdminUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    try {
      const token = getStoredToken('admin');
      if (!token) {
        return undefined;
      }

      const response = await getCurrentUser({ skipErrorHandler: true });
      if (response.success && response.data) {
        return response.data;
      }

      return undefined;
    } catch (_error) {
      clearStoredToken('admin');
      return undefined;
    }
  };

  const fetchSubUserInfo = async (): Promise<
    API.SubAccountProfile | undefined
  > => {
    try {
      const token = getStoredToken('sub');
      if (!token) {
        return undefined;
      }

      const response = await getCurrentSubAccount({ skipErrorHandler: true });
      if (response.success && response.data) {
        return response.data;
      }

      return undefined;
    } catch (_error) {
      clearStoredToken('sub');
      return undefined;
    }
  };

  const scope = resolveRouteAuthScope(history.location.pathname);

  if (scope === 'sub') {
    const currentSubAccount = await fetchSubUserInfo();
    return {
      currentAuthScope: scope,
      currentSubAccount,
      fetchAdminUserInfo,
      fetchSubUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  if (scope === 'admin' && history.location.pathname !== ADMIN_LOGIN_PATH) {
    const currentUser = await fetchAdminUserInfo();
    return {
      currentAuthScope: scope,
      currentUser,
      fetchAdminUserInfo,
      fetchSubUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    currentAuthScope: scope,
    fetchAdminUserInfo,
    fetchSubUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const displayName =
    initialState?.currentUser?.name || initialState?.currentSubAccount?.name;

  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: displayName,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const scope = resolveRouteAuthScope(history.location.pathname);

      if (scope === 'public') {
        return;
      }

      if (scope === 'sub' && !initialState?.currentSubAccount) {
        if (hasPendingScopedIdentity('sub', initialState)) {
          return;
        }

        redirectToLogin('sub', true);
        return;
      }

      if (scope === 'admin' && !initialState?.currentUser) {
        if (hasPendingScopedIdentity('admin', initialState)) {
          return;
        }

        redirectToLogin('admin', true);
      }
    },
    menuHeaderRender: undefined,
    childrenRender: (children) => (
      <ScopedSessionSync>{children}</ScopedSessionSync>
    ),
    ...initialState?.settings,
  };
};

const authHeaderInterceptor = (url: string, options: any) => {
  const scope = resolveApiAuthScope(url);
  const token = getStoredToken(scope);
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  return {
    url,
    options: {
      ...options,
      interceptors: true,
      headers: {
        ...options.headers,
        ...authHeader,
      },
    },
  };
};

const getResponsePathname = (responseUrl: string): string => {
  if (!responseUrl) {
    return '';
  }

  try {
    return new URL(responseUrl).pathname;
  } catch (_error) {
    return responseUrl;
  }
};

const getAxiosResponseUrl = (response: any): string =>
  response?.request?.responseURL ||
  response?.config?.url ||
  response?.url ||
  '';

const getAxiosResponseContentType = (response: any): string => {
  const headers = response?.headers;
  if (!headers) {
    return '';
  }

  if (typeof headers.get === 'function') {
    return headers.get('content-type') || '';
  }

  return headers['content-type'] || headers['Content-Type'] || '';
};

const buildHtmlApiError = (response: any): Error => {
  const responseUrl = getAxiosResponseUrl(response);
  const pathname = getResponsePathname(responseUrl);
  const status = Number(response?.status || 0);
  let message = '接口返回了非 JSON 错误页，请稍后重试。';

  if (status === 404) {
    message = pathname.startsWith('/api/paypal-accounts/')
      ? '请求的 PayPal 账号不存在、已软删除或当前不可访问，请返回账号列表重新选择。'
      : '请求的资源不存在或当前不可访问，请返回列表页重新选择。';
  } else if (status >= 500) {
    message = '接口返回了 HTML 错误页，请先检查后端服务状态与站点路由。';
  }

  const error: any = new Error(message);
  error.name = 'ApiHtmlError';
  error.info = {
    message,
    status,
    data: {
      url: responseUrl,
      content_type: getAxiosResponseContentType(response),
    },
  };

  return error;
};

const responseInterceptor = async (response: any) => {
  const status = Number(response?.status || 0);
  const responseUrl = getAxiosResponseUrl(response);

  if (status === 401) {
    const scope = resolveApiAuthScope(responseUrl);
    clearStoredToken(scope);

    if (resolveRouteAuthScope(history.location.pathname) === scope) {
      redirectToLogin(scope, true);
    }
  }

  const contentType = getAxiosResponseContentType(response);
  const pathname = getResponsePathname(responseUrl);
  const isOk = status >= 200 && status < 300;

  if (
    !isOk &&
    pathname.startsWith('/api/') &&
    contentType.includes('text/html')
  ) {
    throw buildHtmlApiError(response);
  }

  return response;
};

declare const API_BASE_URL: string;
const apiBaseUrl = process.env.NODE_ENV === 'production' ? API_BASE_URL : '';

export const request: RequestConfig = {
  timeout: 30000,
  baseURL: apiBaseUrl,
  requestInterceptors: [authHeaderInterceptor as any],
  responseInterceptors: [responseInterceptor as any],
  errorConfig: {
    errorThrower: (res) => {
      const { success, message, data, errors, error_code, reasons } =
        res as unknown as API.Response;
      if (!success) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = { message, data, errors, error_code, reasons };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) {
        throw error;
      }

      throw error;
    },
  },
};
